export interface EventDescriptor {
  eventName: string
  tags: string[]
  aliases?: string[]
}

export type EventMap = Record<string, EventDescriptor[]>

// @EventList declarations.
export const SWING_EVENT_MAP: EventMap = {
  TEXT: [
    { eventName: 'focusGained', tags: ['onFocusGained', 'event.focusGained'], aliases: ['focus'] },
    { eventName: 'focusLost', tags: ['onFocusLost', 'event.focusLost'], aliases: ['blur'] },
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
    { eventName: 'textEdited', tags: ['event.textEdited'], aliases: [] },
  ],
  TILES: [
    { eventName: 'focusGained', tags: ['onFocusGained', 'event.focusGained'], aliases: ['focus'] },
    { eventName: 'focusLost', tags: ['onFocusLost', 'event.focusLost'], aliases: ['blur'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'], aliases: [] },
  ],
  SLIDER: [
    { eventName: 'focusGained', tags: ['onFocusGained', 'event.focusGained'], aliases: ['focus'] },
    { eventName: 'focusLost', tags: ['onFocusLost', 'event.focusLost'], aliases: ['blur'] },
  ],
  TABLE: [
    { eventName: 'selectionChanged', tags: ['onSelectionChanged', 'event.selectionChanged'], aliases: ['onSelectionChanged', 'selectionChange'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'], aliases: [] },
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
  ],
  EDITABLE: [
    { eventName: 'selectionChanged', tags: ['onSelectionChanged', 'event.selectionChanged'], aliases: ['onSelectionChanged', 'selectionChange'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'], aliases: [] },
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
  ],
  GROUPTABLE: [
    { eventName: 'selectionChanged', tags: ['onSelectionChanged', 'event.selectionChanged'], aliases: ['onSelectionChanged', 'selectionChange'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'], aliases: [] },
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
  ],
  TREETABLE: [
    { eventName: 'selectionChanged', tags: ['onSelectionChanged', 'event.selectionChanged'], aliases: ['onSelectionChanged', 'selectionChange'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'], aliases: [] },
  ],
  WORD: [
    { eventName: 'fill', tags: ['event.fill'] },
    { eventName: 'fileChange', tags: ['event.fileChanged', 'fileChanged'], aliases: ['file-change'] },
    { eventName: 'onChange', tags: ['event.onChange', 'onChange'], aliases: ['change'] },
  ],
  SVG: [
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
  ],
  FILEATTACHMENTS: [
    { eventName: 'selectionChanged', tags: ['event.selectionChanged', 'onSelectionChanged'], aliases: ['onSelectionChanged', 'selectionChange'] },
  ],
  MULTISELECT: [
    { eventName: 'onSuggest', tags: ['onSuggest', 'event.onSuggest'] },
  ],
  BOOLEAN: [
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
  ],
  CHECKBOX: [
    { eventName: 'onChange', tags: ['onChange', 'event.onChange'], aliases: ['change'] },
  ],
  MEDIAWALL: [
    { eventName: 'onImport', tags: ['onImport', 'event.onImport'], aliases: ['import'] },
    { eventName: 'inlineDoFirstAction', tags: ['inlineDoFirstAction', 'onInlineDoFirstAction'] },
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
  ],
  DIAGRAM: [
    { eventName: 'inlineMenuShow', tags: ['onInlineMenuShow', 'inlineMenuShow', 'event.inlineMenuShow'], aliases: ['onInlineMenuShow'] },
  ],
  ACTION: [
    { eventName: 'dialogClosed', tags: ['onDialogClosed', 'event.dialogClosed'] },
    { eventName: 'dialogClosedAndReturn', tags: ['onDialogClosedAndReturn', 'event.dialogClosedAndReturn'] },
    { eventName: 'chainCalled', tags: ['event.chainCalled', 'chainCalled'] },
  ],
  PANEL: [
    { eventName: 'onClick', tags: ['onClick', 'event.onClick'], aliases: ['click'] },
  ],
  PAGE: [
    { eventName: 'onClick', tags: ['onClick', 'event.onClick'], aliases: ['click'] },
  ],
  OVERLAYPAGE: [
    { eventName: 'onClick', tags: ['onClick', 'event.onClick'], aliases: ['click'] },
  ],
}

/**
 * Map: client/DOM event name (used in invokeListeners) -> Swing names (Events.java).
 */
export const LISTENER_EVENT_ALIASES: Record<string, string[]> = {
  mount: ['onLoad', 'load'],
  unmount: ['onUnload', 'unload'],
  blur: ['focusLost'],
  focus: ['focusGained'],
  change: ['onChange'],
  click: ['onClick'],
  selectionChange: ['selectionChanged', 'onSelectionChanged'],
  keyPress: ['keyPressed'],
  keyRelease: ['keyReleased'],
  keyTyped: ['keyTyped'],
  fileChange: ['fileChanged'],
  hover: ['onHover'],
  inlineMenuShow: ['onInlineMenuShow', 'inlineMenuShow'],
  inlineDoFirstAction: ['inlineDoFirstAction'],
  onSuggest: ['onSuggest'],
  onImport: ['onImport'],
}

/**
 * Event names to match when looking up listeners for a fired event
 */
export function getListenerEventNamesToMatch(firedEventName: string): string[] {
  const aliases = LISTENER_EVENT_ALIASES[firedEventName]
  return aliases ? [firedEventName, ...aliases] : [firedEventName]
}
